<template>
    <div class="mcq-score">
        <div class="mcq-score-bar">
            <span class="mcq-score-bar-title">{{ faculty ? "Students Score" : "Your Score" }}</span>
            <b-button variant="light" size="sm" class="mcq-score-close" title="Close" @click="$emit('close')">
                <i class="fas fa-times"></i>
            </b-button>
        </div>
        <div v-if="loading" class="mcq-score-loading">
            <b-spinner small variant="primary" class="mr-2"></b-spinner> Loading score...
        </div>
        <div v-else-if="error" class="text-danger text-center py-3">{{ error }}</div>

        <!-- Student: own score  -->
        <div v-else-if="!faculty" class="mcq-score-card text-center">
            <div class="mcq-score-title">{{ exam.name }}</div>
            <div class="mcq-score-big">
                {{ formatMark(result.score) }}<span> / {{ formatMark(result.total) }}</span>
            </div>
            <div class="mcq-score-percent">{{ percent(result.score, result.total) }}%</div>
            <div class="mcq-score-stats">
                <div class="mcq-stat mcq-stat-success">
                    <div class="mcq-stat-value">{{ result.correct }}</div>
                    <div class="mcq-stat-label">Correct</div>
                </div>
                <div class="mcq-stat mcq-stat-danger">
                    <div class="mcq-stat-value">{{ result.wrong }}</div>
                    <div class="mcq-stat-label">Wrong</div>
                </div>
                <div class="mcq-stat">
                    <div class="mcq-stat-value">{{ result.unanswered }}</div>
                    <div class="mcq-stat-label">Not answered</div>
                </div>
            </div>
            <div class="mcq-score-muted" v-if="result.submitted_at">
                Submitted on {{ formatDate(result.submitted_at) }}
            </div>
            <b-button variant="primary" size="sm" class="mt-3" @click="$bvModal.show('mcqOwnAnswerSheetModal')">
                View answer sheet
            </b-button>

            <b-modal id="mcqOwnAnswerSheetModal" size="lg" scrollable hide-footer title="Your Answer Sheet">
                <McqAnswerSheet :exam="exam" :total="result.total" :score="result.score" />
            </b-modal>
        </div>

        <!-- Faculty: all students  -->
        <div v-else>
            <div class="mcq-score-head">
                <div class="mcq-score-title">{{ exam.name }}</div>
                <div class="mcq-score-chips">
                    <span class="mcq-score-chip">{{ result.question_count }} Questions</span>
                    <span class="mcq-score-chip">Total mark {{ formatMark(result.total) }}</span>
                    <span class="mcq-score-chip mcq-score-chip-success">{{ submittedStudents.length }} / {{ students.length }}
                        submitted</span>                </div>
                <b-form-input v-model="search" size="sm" class="mcq-score-search" placeholder="Search student"></b-form-input>
                <b-button size="sm" variant="primary" class="ml-auto" @click="$bvModal.show('mcqDownloadModal')">
                    <i class="fas fa-download mr-1"></i> Download
                </b-button>
            </div>

            <b-modal id="mcqDownloadModal" hide-footer centered size="md" :title="'Reports for ' + exam.name">
                <table class="table table-striped table-bordered mb-0" style="width:100%;">
                    <tbody>
                        <tr>
                            <th class="p-2">
                                Score card of all students
                                <div class="mcq-score-download-hint">Status, correct, wrong and score of every student</div>
                            </th>
                            <td class="p-2 text-center align-middle">
                                <b-button variant="danger" :disabled="!!downloading" @click="downloadPdf('mcqscorecard', 'Score Card')">
                                    <b-spinner v-if="downloading == 'mcqscorecard'" small class="mr-1"></b-spinner>
                                    Download PDF
                                </b-button>
                            </td>
                        </tr>
                        <tr>
                            <th class="p-2">
                                Answer sheet of all students
                                <div class="mcq-score-download-hint">Submitted students' answer sheets back to back</div>
                            </th>
                            <td class="p-2 text-center align-middle">
                                <b-button variant="danger" :disabled="!!downloading || !submittedStudents.length"
                                    @click="downloadPdf('mcqanswersheets', 'Answer Sheets')">
                                    <b-spinner v-if="downloading == 'mcqanswersheets'" small class="mr-1"></b-spinner>
                                    Download PDF
                                </b-button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </b-modal>
            <table class="table table-bordered table-striped">
                <thead class="thead-dark text-left">
                    <tr>
                        <th>#</th>
                        <th>Roll No</th>
                        <th>Student</th>
                        <th>Status</th>
                        <th>Score</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="!filteredStudents.length">
                        <td colspan="5" class="text-center text-muted">No students found</td>
                    </tr>
                    <tr v-for="(student, index) in filteredStudents" :key="student.id">
                        <td>{{ index + 1 }}</td>
                        <td>{{ student.roll_number || "-" }}</td>
                        <td>{{ student.first_name || "" }} {{ student.last_name || "" }}</td>
                        <td>
                            <span :class="['mcq-status', 'mcq-status-' + studentStatus(student).key]">
                                {{ studentStatus(student).label }}
                            </span>
                        </td>
                        <td>
                            <template v-if="studentStatus(student).key == 'submitted'">
                                {{ formatMark(student.pivot.score) }} / {{ formatMark(result.total) }}
                                <a href="#" class="mcq-score-sheet-link" @click.prevent="openAnswerSheet(student)">
                                    View answer sheet
                                </a>
                            </template>
                            <span v-else class="text-muted">-</span>
                        </td>
                    </tr>
                </tbody>
            </table>

            <b-modal id="mcqAnswerSheetModal" size="lg" scrollable hide-footer title="Answer Sheet"
                @hidden="sheetStudent = null">
                <McqAnswerSheet v-if="sheetStudent" :key="sheetStudent.id" :exam="exam" :student="sheetStudent"
                    :total="result.total" />
            </b-modal>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import McqAnswerSheet from "./McqAnswerSheet.vue";
import { formatMark } from "./mcqSheet.js";

export default {
    components: { McqAnswerSheet },
    // faculty: true lists every student, otherwise the logged in student's own score
    props: ["exam", "faculty"],
    data() {
        return {
            loading: true,
            error: "",
            result: {},
            students: [],
            search: "",
            sheetStudent: null,
            downloading: "", // report being downloaded
        };
    },
    async created() {
        await this.loadScore();
    },
    computed: {
        submittedStudents() {
            return this.students.filter((student) => this.studentStatus(student).key == "submitted");
        },
        filteredStudents() {
            const search = this.search.trim().toLowerCase();
            if (!search) return this.students;
            return this.students.filter((student) =>
                `${student.roll_number || ""} ${student.first_name || ""} ${student.last_name || ""}`
                    .toLowerCase().includes(search)
            );
        },
    },
    methods: {
        async loadScore() {
            this.loading = true;
            this.error = "";
            const examApi = this.$store.getters.getAPIKey.examination + `/${this.exam.id}`;
            try {
                // mcqresult also scores any submitted student not scored yet, so load it before the students
                const result = await this.$axios.get(`${examApi}/mcqresult`);
                this.result = result.data;
                if (this.faculty) {
                    const participants = await this.$axios.get(`${examApi}/examparticipants`);
                    this.students = participants.data.students || [];
                }
            } catch (error) {
                this.error = (error.response && error.response.data && error.response.data.message) ||
                    "Failed to load score, please try again.";
            }
            this.loading = false;
        },
        // report: mcqscorecard | mcqanswersheets
        async downloadPdf(report, title) {
            this.downloading = report;
            const url = this.$store.getters.getAPIKey.examination + `/${this.exam.id}/${report}/pdfexport`;
            await this.$axios
                .post(url, {}, { responseType: "blob" })
                .then((response) => {
                    const fileUrl = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement("a");
                    link.href = fileUrl;
                    link.setAttribute("download", `${title}-${this.exam.name}.pdf`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(fileUrl);
                })
                .catch(() => {
                    this.$toast.error("Failed to download, Please try again", {
                        position: "top",
                        duration: 3000
                    });
                });
            this.downloading = "";
        },
        openAnswerSheet(student) {
            this.sheetStudent = student;
            this.$bvModal.show("mcqAnswerSheetModal");
        },
        studentStatus(student) {
            const pivot = student.pivot || {};
            if (pivot.end_at) return { key: "submitted", label: "Submitted" };
            if (pivot.starts_at) return { key: "started", label: "Not submitted" };
            return { key: "absent", label: "Not attended" };
        },
        formatMark,
        percent(score, total) {
            if (!Number(total)) return 0;
            return Math.max(0, Math.round((Number(score) / Number(total)) * 100));
        },
        formatDate(date) {
            return moment.utc(date, "YYYY-MM-DD HH:mm:ss").local().format("DD/MM/YYYY hh:mm a");
        },
    },
};
</script>

<style scoped>
.mcq-score {
    max-width: 1100px;
    margin: 0 auto;
    font-size: 13px;
    color: #2d3748;
}

.mcq-score-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    margin-bottom: 10px;
    background: linear-gradient(135deg, #eef4ff 0%, #f8fbff 100%);
    border: 1px solid #dbe6fb;
    border-radius: 8px;
}

.mcq-score-bar-title {
    font-size: 15px;
    font-weight: 700;
    color: #1a365d;
}

.mcq-score-close {
    width: 28px;
    height: 28px;
    padding: 0;
    border-radius: 6px;
}

.mcq-score i {
    font-size: 12px !important;
}

.mcq-score-card {
    max-width: 460px;
    margin: 24px auto 0;
    padding: 20px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
}

.mcq-score-loading {
    text-align: center;
    padding: 24px 0;
    color: #718096;
}

.mcq-score-title {
    font-size: 15px;
    font-weight: 700;
    color: #1a365d;
}

.mcq-score-big {
    margin-top: 10px;
    font-size: 40px;
    font-weight: 800;
    line-height: 1.1;
    color: #3b6fd8;
}

.mcq-score-big span {
    font-size: 20px;
    font-weight: 600;
    color: #718096;
}

.mcq-score-percent {
    font-size: 14px;
    font-weight: 600;
    color: #4a5568;
}

.mcq-score-stats {
    display: flex;
    justify-content: center;
    gap: 10px;
    margin: 14px 0 10px;
}

.mcq-stat {
    min-width: 90px;
    padding: 8px 10px;
    background: #f7f9fc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
}

.mcq-stat-value {
    font-size: 18px;
    font-weight: 700;
}

.mcq-stat-label {
    font-size: 11px;
    color: #718096;
}

.mcq-stat-success .mcq-stat-value {
    color: #2f9e55;
}

.mcq-stat-danger .mcq-stat-value {
    color: #dc3545;
}

.mcq-score-muted {
    font-size: 12px;
    color: #718096;
}

.mcq-score-head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
    margin-bottom: 8px;
}

.mcq-score-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.mcq-score-search {
    width: 220px;
}

.mcq-score-chip {
    padding: 1px 8px;
    font-size: 11px;
    font-weight: 600;
    line-height: 18px;
    color: #4a5568;
    background: #edf2f7;
    border-radius: 999px;
}

.mcq-score-chip-success {
    color: #22753a;
    background: #e3f6e8;
}

.mcq-score-download-hint {
    margin-left: 22px;
    font-size: 11px;
    color: #718096;
}

.mcq-score-sheet-link {
    margin-left: 10px;
    font-size: 12px;
    font-weight: 600;
}

.mcq-status {
    padding: 1px 8px;
    font-size: 11px;
    font-weight: 600;
    border-radius: 999px;
}

.mcq-status-submitted {
    color: #22753a;
    background: #e3f6e8;
}

.mcq-status-started {
    color: #b7791f;
    background: #fdf3e1;
}

.mcq-status-absent {
    color: #b42318;
    background: #fdecea;
}
</style>
